/**
 * Google Analytics and Search Console Integration for LogCrest Production
 * Handles GA4, GTM, and Search Console setup
 */

import React, { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { SEO_CONFIG } from '../services/seoService';

const GoogleAnalytics = () => {
  const location = useLocation();
  const isProduction = process.env.NODE_ENV === 'production';

  const trackPageView = useCallback(() => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('config', SEO_CONFIG.googleAnalyticsId, {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname
      });
    }

    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: 'page_view',
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname
      });
    }
  }, [location.pathname]);

  const initializeGA4 = () => {
    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${SEO_CONFIG.googleAnalyticsId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', SEO_CONFIG.googleAnalyticsId, {
      page_title: document.title,
      page_location: window.location.href,
      send_page_view: true
    });
  };

  const initializeGTM = () => {
    // Load Google Tag Manager script
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${SEO_CONFIG.googleTagManagerId}');
    `;
    document.head.appendChild(script);
  };

  useEffect(() => {
    if (!isProduction) return;

    // Initialize Google Analytics 4
    initializeGA4();
    
    // Initialize Google Tag Manager
    initializeGTM();
    
    // Track page view
    trackPageView();
  }, [location.pathname, isProduction, trackPageView]);

  // Don't render anything in development
  if (!isProduction) {
    return null;
  }

  return (
    <>
      {/* Google Tag Manager (noscript) */}
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${SEO_CONFIG.googleTagManagerId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
          title="Google Tag Manager"
        />
      </noscript>
      
      {/* Google Search Console Verification */}
      <meta name="google-site-verification" content={SEO_CONFIG.googleSearchConsoleId} />
    </>
  );
};

export default GoogleAnalytics;
